import { AddressModel, AddressSuggestionsProvider } from '@servicetitan/onboarding-ui';
import { inject, injectable, symbolToken } from '@servicetitan/react-ioc';
import axios, { AxiosPromise } from 'axios';

interface AddressVerificationResult {
    result?: boolean;
    errorMessage?: string;
    suggestedAddresses?: AddressInput[] | undefined;
}

interface AddressInput {
    test: string;
    street?: string | undefined;
    unit?: string | undefined;
    country?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    zip?: string | undefined;
}

interface AvalaraValidationApi {
    verifyAddress(model: AddressInput): AxiosPromise<AddressVerificationResult>;
}

export const AVALARA_VALIDATION_API_SYMBOL_TOKEN = symbolToken<AvalaraValidationApi>(
    'AVALARA_VALIDATION_API_SYMBOL_TOKEN'
);

@injectable()
export class AvalaraSuggestionsProvider implements AddressSuggestionsProvider {
    constructor(
        @inject(AVALARA_VALIDATION_API_SYMBOL_TOKEN)
        private companyProfileApi: AvalaraValidationApi
    ) {}

    getSuggestions = async (address: AddressInput) => {
        try {
            const {
                data: { result, errorMessage, suggestedAddresses },
            } = await this.companyProfileApi.verifyAddress(address);

            if (!result) {
                return errorMessage;
            }

            return suggestedAddresses?.map(addr => {
                const usLongZipRg = /^\d{5}-\d{4}$/;
                if (addr.zip?.match(usLongZipRg)) {
                    addr.zip = addr.zip.split('-')[0];
                }
                return {
                    key: this.addressToString(addr),
                    title: this.addressToString(addr),
                    value: { ...addr },
                };
            });
        } catch (e) {
            if (axios.isAxiosError(e) && e.response?.data?.Error?.Message) {
                return e.response?.data?.Error?.Message;
            }
            throw e;
        }
    };

    private addressToString = ({ street, unit, city, state, zip, country }: AddressModel) => {
        const addressArray: string[] = [];

        const appendPart = (prefix: string, part?: string) => {
            if (part) {
                addressArray.push(prefix, part);
            }
        };

        appendPart('', street);
        appendPart(' ', unit ? '#' + unit : undefined);
        appendPart(', ', city);
        appendPart(', ', state);
        appendPart(' ', zip);
        appendPart(' ', country);

        return addressArray.join('');
    };
}
